"use client";

import { usePathname, useRouter } from "next/navigation";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Boxes,
  ChevronDown,
  PackageCheck,
  PlusSquare,
  Ticket,
  Menu,
  ClipboardList,
  Building2,
  Clock,
  Truck,
  Eye,
  Percent,
  ToggleRight,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import Image from "next/image";
import ccLogo from "../assets/icons/Cycle-Circle-Logo-Orange-1.png";
import { useEffect, useRef, useState, JSX } from "react";
import gsap from "gsap";
import Link from "next/link";

const B2CSidebar = ({
  isSidebarOpen,
  toggleSidebar,
  setIsSidebarOpen,
}: {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  setIsSidebarOpen: (id: boolean) => void;
}) => {
  const pathname = usePathname();
  const [isInventoryOpen, setIsInventoryOpen] = useState(false);
  const navRef = useRef<HTMLDivElement | null>(null);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setIsSidebarOpen(false); // Close sidebar when clicking outside
      }
    };

    if (isSidebarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSidebarOpen, setIsSidebarOpen]);

  // Prefetch B2C routes when sidebar opens
  useEffect(() => {
    if (isSidebarOpen && router) {
      router.prefetch("/select-business");
      router.prefetch("/b2c/dashboard");
      router.prefetch("/b2c/orders");
      router.prefetch("/b2c/pre-orders");
      router.prefetch("/b2c/all-inventory");
      router.prefetch("/b2c/carts");
      router.prefetch("/b2c/brands");
      router.prefetch("/b2c/manage-inventory");
      router.prefetch("/b2c/coupons");
      router.prefetch("/b2c/wallets");
    }
  }, [isSidebarOpen, router]);

  useEffect(() => {
    if (navRef.current) {
      gsap.fromTo(
        navRef.current.children,
        { x: -50, opacity: 0 },
        { x: 0, opacity: 1, stagger: 0.1, ease: "power2.out", duration: 0.6 }
      );
    }
  }, []);

  return (
    <div
      ref={sidebarRef}
      className={`bg-white h-full shadow-md flex flex-col transition-all duration-300 ${
        isSidebarOpen ? "w-64" : "w-fit"
      }`}
    >
      <Card
        className={cn(
          "fixed left-0 top-0 h-full border-none  bg-gradient-to-b rounded-none from-gray-900 to-gray-800 text-white shadow-xl z-50 transition-transform duration-300",
          isSidebarOpen
            ? "translate-x-0 w-72 lg:w-64 py-4 px-4"
            : "-translate-x-full lg:translate-x-0 w-auto py-4 px-0"
        )}
      >
        {/* Logo */}
        {isSidebarOpen ? (
          <div className="flex items-center justify-center mb-1">
            <Image
              src={ccLogo}
              priority
              alt="cc-logo"
              width={75}
              height={50}
              className="object-contain"
            />
          </div>
        ) : (
          <div className="flex items-center justify-center px-4 mb-1">
            <button
              onClick={toggleSidebar}
              className="text-gray-600 cursor-pointer"
            >
              <Menu size={35} className=" text-white" />
            </button>
          </div>
        )}

        {/* Navigation Links */}
        <nav
          ref={navRef}
          className="space-y-1 overflow-y-auto max-h-[calc(100vh-130px)] pr-1 custom-scrollbar"
        >
          <div className="pb-1 border-b border-gray-600 mb-2">
            <SidebarLink
              href="/select-business"
              icon={<Building2 size={20} />}
              text="Switch Business"
              pathname={pathname}
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
            />
          </div>
          <SidebarLink
            href="/b2c/dashboard"
            icon={<LayoutDashboard size={20} />}
            text="Dashboard"
            pathname={pathname}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
          <SidebarLink
            href="/b2c/orders"
            icon={<ClipboardList size={20} />}
            text="Orders"
            pathname={pathname}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
          <SidebarLink
            href="/b2c/pre-orders"
            icon={<Clock size={20} />}
            text="Pre-Orders"
            pathname={pathname}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
          <SidebarLink
            href="/b2c/all-inventory"
            icon={<Boxes size={20} />}
            text="All Inventory"
            pathname={pathname}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
          <SidebarLink
            href="/b2c/carts"
            icon={<ShoppingCart size={20} />}
            text="Carts"
            pathname={pathname}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />

          {/* Manage Inventory (Collapsible) */}
          <button
            onClick={() => setIsInventoryOpen(!isInventoryOpen)}
            className={`flex items-center ${
              isSidebarOpen
                ? "justify-between hover:bg-gray-700"
                : "justify-center"
            } w-full px-4 py-3 rounded-lg text-gray-300 hover:text-white transition-all`}
          >
            <div className="flex items-center space-x-3">
              <Boxes
                size={20}
                className="text-gray-400 transition-all hover:text-white hover:scale-140"
                onClick={() => !isInventoryOpen && toggleSidebar()}
              />
              {isSidebarOpen && (
                <span className="font-medium text-[14px]">
                  Manage Inventory
                </span>
              )}
            </div>
            {isSidebarOpen && (
              <ChevronDown
                size={18}
                className={`transition-transform ${
                  isInventoryOpen ? "rotate-180" : ""
                }`}
              />
            )}
          </button>
          {isInventoryOpen && isSidebarOpen && (
            <div className="pl-8 space-y-2">
              <SidebarLink
                href="/b2c/brands"
                icon={<PackageCheck size={16} />}
                text="Configure Brand"
                pathname={pathname}
                isSidebarOpen={isSidebarOpen}
                setIsSidebarOpen={setIsSidebarOpen}
              />
              <SidebarLink
                href="/b2c/manage-inventory"
                icon={<PlusSquare size={16} />}
                text="Manage Inventory"
                pathname={pathname}
                isSidebarOpen={isSidebarOpen}
                setIsSidebarOpen={setIsSidebarOpen}
              />
            </div>
          )}

          <SidebarLink
            href="/b2c/collections"
            icon={<Package size={20} />}
            text="Collections"
            pathname={pathname}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
          <SidebarLink
            href="/b2c/coupons"
            icon={<Ticket size={20} />}
            text="Coupons"
            pathname={pathname}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
          <SidebarLink
            href="/b2c/free-shipping"
            icon={<Truck size={20} />}
            text="Free Shipping"
            pathname={pathname}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />

          <SidebarLink
            href="/b2c/product-visibility"
            icon={<Eye size={20} />}
            text="Product Visibility"
            pathname={pathname}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
          <SidebarLink
            href="/b2c/sale"
            icon={<Percent size={20} />}
            text="Sale"
            pathname={pathname}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
          <SidebarLink
            href="/b2c/switch-to-admin"
            icon={<ToggleRight size={20} />}
            text="Swith to Admin"
            pathname={pathname}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
        </nav>
      </Card>
    </div>
  );
};

const SidebarLink = ({
  href,
  icon,
  text,
  pathname,
  isSidebarOpen,
  setIsSidebarOpen,
}: {
  href: string;
  icon: JSX.Element;
  text: string;
  pathname: string;
  isSidebarOpen: boolean;
  setIsSidebarOpen: (id: boolean) => void;
}) => {
  const isActive = pathname === href;
  const isSwitchBusiness = text === "Switch Business";

  return (
    <Link href={href} passHref>
      <button
        onClick={() => {
          if (isSidebarOpen) {
            setIsSidebarOpen(false);
          }
        }}
        className={cn(
          "w-full cursor-pointer whitespace-nowrap flex items-center space-x-3 px-4 py-2.5 my-1 rounded-lg transition-all group",
          isActive
            ? isSidebarOpen
              ? isSwitchBusiness
                ? "bg-orange-100 text-orange-600 shadow-md"
                : "bg-white text-black shadow-md"
              : isSwitchBusiness
              ? "text-orange-500 justify-center"
              : "text-white justify-center"
            : isSidebarOpen
            ? isSwitchBusiness
              ? "text-orange-500 hover:bg-orange-200 hover:text-orange-700"
              : "text-gray-300 hover:bg-gray-700 hover:text-white"
            : isSwitchBusiness
            ? "text-orange-500 hover:text-orange-600 justify-center"
            : "text-gray-300 hover:text-white justify-center"
        )}
      >
        <span
          className={cn(
            "transition-all duration-300",
            isActive
              ? isSidebarOpen
                ? isSwitchBusiness
                  ? "text-orange-600"
                  : "text-black"
                : isSwitchBusiness
                ? "text-orange-500"
                : "text-white group-hover:text-white group-hover:scale-140"
              : isSidebarOpen
              ? isSwitchBusiness
                ? "text-orange-500 group-hover:text-orange-600"
                : "text-gray-400 group-hover:text-white"
              : isSwitchBusiness
              ? "text-orange-500 group-hover:text-orange-600 group-hover:scale-140"
              : "text-gray-400 group-hover:text-white group-hover:scale-140"
          )}
        >
          {icon}
        </span>
        {isSidebarOpen && (
          <span
            className={cn(
              "transition-all font-medium text-[14px]",
              isActive
                ? isSwitchBusiness
                  ? "text-orange-600"
                  : "text-black"
                : isSwitchBusiness
                ? "text-orange-500 group-hover:text-orange-600"
                : "text-gray-300 group-hover:text-white"
            )}
          >
            {text}
          </span>
        )}
      </button>
    </Link>
  );
};

export default B2CSidebar;
